import { Button, Field, Input, Select } from '@bb/ui';
import { useState } from 'react';
import { isReservedExtraKey, RESERVED_EXTRA_KEYS, type IssueLookup } from './pushChecks.ts';
import { errorText } from './PushIssues.tsx';

export type ExtraValue = string | number | boolean;
type ValueType = 'string' | 'number' | 'boolean';

interface Row {
  key: string;
  type: ValueType;
  /** As typed: a number stays text until it is one. */
  text: string;
}

export interface KeyValuePairsEditorProps {
  value: Readonly<Record<string, ExtraValue>>;
  onChange: (next: Record<string, ExtraValue>) => void;
  readOnly?: boolean;
  /** Issues of the last check (`extras.<key>`). */
  issues?: IssueLookup;
}

const TYPES: Array<{ value: ValueType; label: string }> = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'True / false' },
];
const BOOLEANS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
];

const rowsOf = (value: Readonly<Record<string, ExtraValue>>): Row[] =>
  Object.entries(value).map(([key, v]) => ({ key, type: typeof v === 'number' ? 'number' : typeof v === 'boolean' ? 'boolean' : 'string', text: String(v) }));

const parsed = (row: Row): ExtraValue | undefined => {
  if (row.type === 'boolean') return row.text === 'true';
  if (row.type === 'number') return row.text.trim() !== '' && Number.isFinite(Number(row.text)) ? Number(row.text) : undefined;
  return row.text;
};

/** Rows as stored: keys trimmed, empty keys, duplicates and unfinished numbers left out. */
function recordOf(rows: readonly Row[]): Record<string, ExtraValue> {
  const out: Record<string, ExtraValue> = {};
  for (const row of rows) {
    const key = row.key.trim();
    const v = parsed(row);
    if (key && v !== undefined && !(key in out)) out[key] = v;
  }
  return out;
}

/**
 * Key-value pairs the host app receives with the push (`bb.ex`, BB-58/BB-76): text, number or true/false. Keys of the
 * payload itself (`bb`, `aps`, `ab`, …) are refused at once, like the server does; duplicates are flagged.
 */
export function KeyValuePairsEditor({ value, onChange, readOnly = false, issues }: KeyValuePairsEditorProps) {
  const [rows, setRows] = useState<Row[]>(() => rowsOf(value));
  const [synced, setSynced] = useState(() => JSON.stringify(value));
  const incoming = JSON.stringify(value);
  // A change from outside (another locale, a reset) replaces the rows; our own edits come back unchanged.
  if (incoming !== synced) {
    setSynced(incoming);
    if (JSON.stringify(recordOf(rows)) !== incoming) setRows(rowsOf(value));
  }
  const write = (next: Row[]) => {
    setRows(next);
    const record = recordOf(next);
    setSynced(JSON.stringify(record));
    onChange(record);
  };
  const update = (i: number, patch: Partial<Row>) => write(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));

  return (
    <section className="push-kvp" aria-label="Key-value pairs">
      <h4>Key-value pairs</h4>
      <p className="hint">Optional data for the app, e.g. a tracking parameter or a screen to refresh. Reserved: {RESERVED_EXTRA_KEYS.join(', ')}.</p>
      {rows.length === 0 && <p className="hint">No pairs.</p>}
      {rows.map((row, i) => {
        const key = row.key.trim();
        const duplicate = key !== '' && rows.findIndex((r) => r.key.trim() === key) !== i;
        const local = isReservedExtraKey(key) ? `"${key}" is reserved for the push payload.` : duplicate ? `"${key}" is already used.` : undefined;
        const server = key && !local ? errorText(issues?.(`extras.${key}`) ?? { errors: [], warnings: [] }) : undefined;
        const numberError = row.type === 'number' && parsed(row) === undefined ? 'Enter a number.' : undefined;
        return (
          <div key={i} className="push-kvp__row" role="group" aria-label={`Pair ${i + 1}`}>
            <Field label={`Pair ${i + 1} key`} error={local ?? server}>
              <Input value={row.key} maxLength={100} spellCheck={false} disabled={readOnly} onChange={(e) => update(i, { key: e.target.value })} />
            </Field>
            <Field label={`Pair ${i + 1} type`}>
              <Select options={TYPES} value={row.type} disabled={readOnly} onChange={(e) => update(i, { type: e.target.value as ValueType, text: e.target.value === 'boolean' ? 'true' : row.type === 'boolean' ? '' : row.text })} />
            </Field>
            <Field label={`Pair ${i + 1} value`} error={numberError}>
              {row.type === 'boolean' ? (
                <Select options={BOOLEANS} value={row.text === 'false' ? 'false' : 'true'} disabled={readOnly} onChange={(e) => update(i, { text: e.target.value })} />
              ) : (
                <Input value={row.text} inputMode={row.type === 'number' ? 'decimal' : undefined} maxLength={1000} disabled={readOnly} onChange={(e) => update(i, { text: e.target.value })} />
              )}
            </Field>
            {!readOnly && (
              <Button size="sm" variant="ghost" onClick={() => write(rows.filter((_, n) => n !== i))}>
                Remove pair {i + 1}
              </Button>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <Button size="sm" onClick={() => setRows([...rows, { key: '', type: 'string', text: '' }])}>
          Add pair
        </Button>
      )}
    </section>
  );
}
